import { useState, useMemo, useEffect, useCallback } from "react"
import { useTenant } from "../../../hooks/useTenant"
import { useMembers } from "../../../hooks/useMembers"
import LevelService from "../../../services/level_service"
import { useClassMembers } from "./useClassMembers"
import { useEvaluationEvents } from "./useEvaluationEvents"
import { useEvaluationSearch } from "./useEvaluationSearch"

/**
 * useEvaluationFormLogic
 * Lógica central do formulário de avaliação. Agora age puramente como orquestrador
 * do estado de loading e composição da busca de membros e eventos.
 */
export const useEvaluationFormLogic = ({ classId, activeMode = "technical" }) => {
    const { idTenant, user } = useTenant()
    const { members: allMembers } = useMembers()

    const [loadingKeys, setLoadingKeys] = useState({
        checkEvent: true,
        classMembers: true,
        objectives: true
    })
    const [levels, setLevels] = useState([])

    const isLoading = useCallback((key) => {
        if (!key) return Object.values(loadingKeys).some(Boolean)
        return !!loadingKeys[key]
    }, [loadingKeys])
    
    const anyLoading = Object.values(loadingKeys).some(Boolean)

    const withLoading = useCallback(async (key, fn) => {
        setLoadingKeys(prev => ({ ...prev, [key]: true }))
        try {
            return await fn()
        } finally {
            setLoadingKeys(prev => ({ ...prev, [key]: false }))
        }
    }, [])

    useEffect(() => {
        if (!idTenant) return
        const loadLevels = async () => {
            try {
                const result = await LevelService.getLevels(idTenant)
                setLevels(result || [])
            } catch (e) {
                console.error("Erro ao carregar níveis:", e)
            }
        }
        loadLevels()
    }, [idTenant])

    const { members: classMembers } = useClassMembers({ classId, withLoading, allMembers })
    
    const { activeEvent, activeTestEvent } = useEvaluationEvents({ idTenant, withLoading })

    const {
        searchText,
        setSearchText,
        combinedMembers,
        evaluationMembers,
        addCandidates,
        showNoAutocompleteResults,
        toggleExcludeMember,
        handleAddMember,
        excludedMemberIds
    } = useEvaluationSearch({ classMembers, allMembers, classId })

    const defaultLevelId = useMemo(() => {
        const first = Array.isArray(levels) && levels.length > 0 ? levels[0] : null
        return first?.id != null ? String(first.id) : ""
    }, [levels])

    const requiresObjectives = activeMode === 'technical'
    const formReady = !loadingKeys.checkEvent && !loadingKeys.classMembers && (!requiresObjectives || !loadingKeys.objectives)

    return {
        isLoading,
        anyLoading,
        formReady,
        withLoading,
        searchText,
        setSearchText,
        levels,
        activeEvent,
        activeTestEvent,
        allMembers: combinedMembers,
        evaluationMembers,
        addCandidates,
        showNoAutocompleteResults,
        defaultLevelId,
        toggleExcludeMember,
        handleAddMember,
        excludedMemberIds,
        classMembers,
        idTenant,
        user
    }
}
